import { useEffect, useRef, useState } from 'react';
import { BrandMark } from '@/Components/Layout/BrandMark';
import { Head, Link, router, usePage } from '@inertiajs/react';
import * as LucideIcons from 'lucide-react';
import {
    Send, ShieldCheck, BarChart3, Sparkles, Workflow, Filter, Users, Globe,
    Webhook, MessageSquare, Mail, ArrowRight, Check, Star, Zap, Building2,
    LineChart, Inbox, Code, ShoppingBag, FileText, BookOpen, Bot, Bell,
    LayoutGrid, FormInput, MousePointer, Activity, TrendingUp, Plug,
    PlayCircle, Clock, Layers, Sun, Moon,
} from 'lucide-react';
import { LocaleSwitcher } from '@/Components/Layout/LocaleSwitcher';
import { useT } from '@/lib/i18n';
import { HeroShowcase } from '@/Components/Landing/HeroShowcase';
import { HeroVideoModal } from '@/Components/Landing/HeroVideoModal';

function lucide(name, fallback) {
    if (!name) return fallback;
    return LucideIcons[name] ?? fallback;
}
import { Button } from '@/Components/UI/Button';
import { Badge } from '@/Components/UI/Badge';
import { cn } from '@/lib/utils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ---------- helpers ---------- */

function intervalLabel(price) {
    if (!price) return '';
    if (price.interval_count === 1) {
        return ({
            day: '/day',
            week: '/week',
            month: '/mo',
            year: '/year',
        })[price.interval_type] ?? `/${price.interval_type}`;
    }
    if (price.interval_type === 'month' && price.interval_count === 12) return '/year';
    return `/${price.interval_count} ${price.interval_type}s`;
}

function useReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!ref.current || visible) return;
        const obs = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setVisible(true),
            { rootMargin: '-60px 0px' },
        );
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, [visible]);
    return [ref, visible];
}

function Reveal({ children, delay = 0, className }) {
    const [ref, visible] = useReveal();
    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={cn(
                'transition-all duration-800 ease-out',
                visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
                className,
            )}
        >
            {children}
        </div>
    );
}

// Typewriter headline with blinking underscore cursor.
function RotatingWord({ words, typeMs = 75, deleteMs = 38, holdMs = 1700, gapMs = 240, className }) {
    const [wordIdx, setWordIdx] = useState(0);
    const [text, setText] = useState('');
    const [phase, setPhase] = useState('typing');

    useEffect(() => {
        if (!words || !words.length) return undefined;
        const target = words[wordIdx] ?? '';
        if (phase === 'typing') {
            if (text.length < target.length) {
                const t = window.setTimeout(() => setText(target.slice(0, text.length + 1)), typeMs);
                return () => window.clearTimeout(t);
            }
            if (words.length <= 1) return undefined;
            const t = window.setTimeout(() => setPhase('deleting'), holdMs);
            return () => window.clearTimeout(t);
        }
        if (phase === 'deleting') {
            if (text.length > 0) {
                const t = window.setTimeout(() => setText(text.slice(0, -1)), deleteMs);
                return () => window.clearTimeout(t);
            }
            const t = window.setTimeout(() => {
                setWordIdx((v) => (v + 1) % words.length);
                setPhase('typing');
            }, gapMs);
            return () => window.clearTimeout(t);
        }
        return undefined;
    }, [text, phase, wordIdx, words, typeMs, deleteMs, holdMs, gapMs]);

    if (!words || !words.length) return null;
    const longest = words.reduce((a, b) => (b.length > a.length ? b : a), '');

    return (
        <span
                className={cn(
                    'relative text-2xl sm:text-5xl lg:text-6xl flex justify-center align-baseline leading-[1.25] pb-[0.22em]',
                    className
                )}
            >
            <span aria-hidden className="invisible whitespace-nowrap">{longest}_</span>
            <span className="absolute inset-0 whitespace-nowrap bg-gradient-to-r from-accent via-violet-500 to-purple-500 bg-clip-text text-transparent km-grad-sweep">
                {text}
                <span aria-hidden className="km-hero-underscore km-hero-underscore-blink" />
            </span>
        </span>
    );
}

// Hero backdrop: aurora blurs, beam, twinkling dots.
function HeroBackdrop() {
    const dots = [
        { x: '8%', y: '18%', d: -2.6, s: 4 },
        { x: '22%', y: '40%', d: -0.4, s: 3 },
        { x: '35%', y: '12%', d: -1.8, s: 5 },
        { x: '48%', y: '60%', d: -3.2, s: 3 },
        { x: '64%', y: '24%', d: -0.9, s: 4 },
        { x: '78%', y: '52%', d: -2.1, s: 5 },
        { x: '88%', y: '20%', d: -1.4, s: 3 },
        { x: '12%', y: '70%', d: -2.8, s: 4 },
        { x: '40%', y: '82%', d: -1.1, s: 3 },
        { x: '58%', y: '78%', d: -0.6, s: 5 },
        { x: '72%', y: '88%', d: -2.4, s: 4 },
        { x: '92%', y: '70%', d: -1.7, s: 3 },
    ];

    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
            {/* Base radial gradients. */}
            <div className="absolute inset-0 [background:radial-gradient(circle_at_70%_-20%,hsl(var(--primary-h)_var(--primary-s)_var(--primary-l)/0.18),transparent_55%),radial-gradient(circle_at_-10%_60%,hsl(var(--primary-h)_var(--primary-s)_var(--primary-l)/0.12),transparent_55%)]" />

            {/* Aurora blurs */}
            <div className="km-aurora-a absolute right-[10%] top-[12%] size-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="km-aurora-b absolute left-[5%] top-[50%] size-96 rounded-full bg-accent/18 blur-3xl" />
            <div className="km-aurora-a absolute left-[6%] top-[10%] size-72 rounded-full bg-violet-400/25 blur-3xl" style={{ animationDelay: '-6s' }} />
            <div className="km-aurora-b absolute right-[6%] bottom-[10%] size-96 rounded-full bg-purple-400/22 blur-3xl" style={{ animationDelay: '-8s' }} />

            {/* Light beam */}
            <div className="absolute -left-1/3 top-1/4 h-[2px] w-[60%] bg-gradient-to-r from-transparent via-accent/60 to-transparent blur-sm km-beam" />

            {/* Twinkling dots */}
            {dots.map((dot, idx) => (
                <span
                    key={idx}
                    className="km-twinkle absolute rounded-full bg-accent shadow-[0_0_10px_currentColor]"
                    style={{
                        left: dot.x,
                        top: dot.y,
                        width: dot.s,
                        height: dot.s,
                        animationDelay: `${dot.d}s`,
                        color: idx % 3 === 0 ? 'rgb(122 90 224 / 0.7)' : 'rgb(242 107 92 / 0.7)',
                    }}
                />
            ))}
        </div>
    );
}

// Live-feed ticker with fade swap.
function AILiveFeed({ items, interval = 3600, fadeMs = 380 }) {
    const [i, setI] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        if (!items || items.length <= 1) return undefined;
        let cycleTimer = null;
        let swapTimer = null;
        const cycle = () => {
            setVisible(false);
            swapTimer = window.setTimeout(() => {
                setI((v) => (v + 1) % items.length);
                setVisible(true);
            }, fadeMs);
        };
        const start = () => { stop(); cycleTimer = window.setInterval(cycle, interval); };
        const stop = () => {
            if (cycleTimer) { window.clearInterval(cycleTimer); cycleTimer = null; }
            if (swapTimer) { window.clearTimeout(swapTimer); swapTimer = null; }
            setVisible(true);
        };
        const onVis = () => (document.hidden ? stop() : start());
        start();
        document.addEventListener('visibilitychange', onVis);
        return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    }, [items, interval, fadeMs]);

    if (!items || !items.length) return null;
    const item = items[i];

    return (
        <div className="mx-auto mt-8 max-w-md">
            <div className="relative overflow-hidden rounded-full border border-border bg-card/60 px-3 py-1.5 text-[12px] backdrop-blur-sm shadow-sm">
                <div className="flex items-center gap-2.5">
                    <span className="grid size-5 shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent to-violet-500 text-white">
                        <Sparkles className="size-2.5" strokeWidth={2.5} />
                    </span>
                    <span className="relative inline-flex size-1.5 shrink-0 rounded-full bg-emerald-500 pulse-dot text-emerald-500" />
                    <div
                        className={cn(
                            'flex min-w-0 flex-1 items-center gap-1.5 transition-all ease-out',
                            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1.5',
                        )}
                        style={{ transitionDuration: `${fadeMs}ms` }}
                    >
                        <span className="truncate font-medium text-foreground">{item.label}</span>
                        {item.meta && (
                            <span className="hidden shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground sm:inline">
                                {item.meta}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------- content ---------- */

const STAT_ICONS = [Inbox, Send, Zap, ShieldCheck];
const STAT_TONES = [
    { bg: 'bg-accent/12', text: 'text-accent', glow: 'bg-accent/30' },
    { bg: 'bg-violet-500/12', text: 'text-violet-500', glow: 'bg-violet-500/25' },
    { bg: 'bg-emerald-500/12', text: 'text-emerald-500', glow: 'bg-emerald-500/25' },
    { bg: 'bg-blue-500/12', text: 'text-blue-500', glow: 'bg-blue-500/25' },
];

// Seed defaults — Admin → Landing editor (DB) overrides every field at runtime.
// Source carries icon refs only; text resolved from lang/en/landing.php at render.
const FEATURE_ICONS = [Send, ShieldCheck, Sparkles, Workflow, Filter, BarChart3, Inbox, FormInput, Plug];
const INTEGRATION_ICONS = [ShoppingBag, ShoppingBag, Webhook, Zap, Code, Plug];
const SHOWCASE_META = [
    { icon: Send,       mock: 'campaign' },
    { icon: Users,      mock: 'subscribers' },
    { icon: Workflow,   mock: 'automation' },
    { icon: LayoutGrid, mock: 'landing' },
    { icon: Plug,       mock: 'integrations' },
    { icon: BarChart3,  mock: 'analytics' },
];

function defaultFeatures(t) {
    return FEATURE_ICONS.map((icon, i) => ({
        icon,
        title: t(`landing.demo.features.${i}.title`),
        desc:  t(`landing.demo.features.${i}.desc`),
    }));
}

function defaultIntegrations(t) {
    return INTEGRATION_ICONS.map((icon, i) => ({
        icon,
        name: t(`landing.demo.integrations.${i}.name`),
        hint: t(`landing.demo.integrations.${i}.hint`),
    }));
}

function defaultTestimonials(t) {
    return [0, 1, 2].map((i) => ({
        quote:  t(`landing.demo.testimonials.${i}.quote`),
        author: t(`landing.demo.testimonials.${i}.author`),
        role:   t(`landing.demo.testimonials.${i}.role`),
    }));
}

function defaultFaqs(t) {
    return [0, 1, 2, 3, 4, 5].map((i) => ({
        q: t(`landing.demo.faqs.${i}.q`),
        a: t(`landing.demo.faqs.${i}.a`),
    }));
}

function defaultShowcases(t) {
    return SHOWCASE_META.map((meta, i) => ({
        ...meta,
        eyebrow: t(`landing.demo.showcases.${i}.eyebrow`),
        title:   t(`landing.demo.showcases.${i}.title`),
        body:    t(`landing.demo.showcases.${i}.body`),
        bullets: [0, 1, 2, 3].map((b) => t(`landing.demo.showcases.${i}.bullets.${b}`)),
    }));
}

/* ---------- inline SVG mocks per section ---------- */

function MockChrome({ children, url }) {
    return (
        <div className="relative w-full max-w-full overflow-hidden rounded-2xl border border-border bg-card shadow-custom shadow-accent/5">
            <div className="flex items-center gap-1.5 border-b border-border/80 px-4 py-2.5">
                <span className="size-2.5 shrink-0 rounded-full bg-destructive/60" />
                <span className="size-2.5 shrink-0 rounded-full bg-warning/60" />
                <span className="size-2.5 shrink-0 rounded-full bg-success/60" />
                <span className="ml-3 truncate font-mono text-[11px] text-muted-foreground">{url || 'app.example.com'}</span>
            </div>
            <div className="overflow-x-auto p-3 sm:p-5">{children}</div>
        </div>
    );
}

function Mock({ kind, url }) {
    const { t } = useT();

    if (kind === 'campaign') {
        const steps = [
            { label: 'Recipients', done: true },
            { label: 'From', done: true },
            { label: 'Subject', done: true },
            { label: 'Content', done: false, active: true },
            { label: 'Tracking', done: false },
            { label: 'Schedule', done: false },
            { label: 'Review', done: false },
        ];
        return (
            <MockChrome url={url}>
                <div className="grid grid-cols-[88px_1fr_120px] gap-3">
                    {/* Step rail */}
                    <div className="space-y-1">
                        {steps.map((s, i) => (
                            <div
                                key={s.label}
                                className={cn(
                                    'flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[9px]',
                                    s.active && 'border-accent/40 bg-accent/5 text-accent font-semibold',
                                    s.done && !s.active && 'border-transparent text-muted-foreground',
                                    !s.active && !s.done && 'border-transparent text-muted-foreground/70',
                                )}
                            >
                                <span className={cn(
                                    'inline-flex size-3.5 shrink-0 items-center justify-center rounded-full text-[8px] font-bold',
                                    s.done ? 'bg-success/15 text-success' : s.active ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground/60',
                                )}>
                                    {s.done ? '✓' : i + 1}
                                </span>
                                <span className="truncate">{s.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Compose canvas */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] font-semibold tracking-tight">{t('landing.mock.compose.campaign_name')}</div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-1.5 py-0.5 text-[8px] font-semibold text-warning">
                                <span className="size-1 rounded-full bg-warning" />{t('landing.mock.compose.draft')}
                            </span>
                        </div>
                        <div className="rounded-md border border-border bg-background p-2">
                            <div className="text-[8px] uppercase tracking-wider text-muted-foreground">{t('landing.mock.compose.subject')}</div>
                            <div className="mt-0.5 flex items-center text-[10px] font-medium text-foreground">
                                <span className="km-typing inline-block">{t('landing.mock.compose.subject_text')}</span>
                                <span className="ml-0.5 inline-block h-2.5 w-px bg-foreground km-cursor-blink" />
                            </div>
                        </div>
                        <div className="rounded-md border border-dashed border-accent/40 bg-accent/5 p-2">
                            <div className="space-y-1">
                                <div className="h-1.5 w-3/4 rounded bg-foreground/60 km-skel" />
                                <div className="h-1.5 w-2/3 rounded bg-foreground/40 km-skel" style={{ animationDelay: '-0.6s' }} />
                                <div className="h-1.5 w-1/2 rounded bg-foreground/30 km-skel" style={{ animationDelay: '-1.2s' }} />
                            </div>
                            <div className="mt-2 grid grid-cols-3 gap-1">
                                <div className="h-6 rounded bg-accent/30 km-bar-grow" />
                                <div className="h-6 rounded bg-accent/40 km-bar-grow" style={{ animationDelay: '-1.8s' }} />
                                <div className="h-6 rounded bg-accent km-bar-grow" style={{ animationDelay: '-3.6s' }} />
                            </div>
                            <div className="mx-auto mt-2 h-4 w-16 rounded bg-accent km-cta-pulse" />
                        </div>
                        <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
                            <span className="relative inline-flex size-1.5 rounded-full bg-success pulse-dot text-success" />
                            {t('landing.mock.compose.preflight')} · <span className="km-digit-tick font-semibold tabular-nums text-foreground">12,481</span> {t('landing.mock.compose.recipients')}
                        </div>
                    </div>

                    {/* AI Copilot panel */}
                    <div className="rounded-md border border-violet-500/30 bg-violet-500/5 p-1.5">
                        <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wider text-violet-500">
                            <Sparkles className="size-2.5" />{t('landing.mock.compose.copilot')}
                        </div>
                        <div className="mt-1 space-y-1">
                            {[
                                t('landing.mock.compose.open_rate_high'),
                                t('landing.mock.compose.open_rate_mid'),
                                t('landing.mock.compose.open_rate_low'),
                            ].map((label, i) => (
                                <div key={label} className="rounded border border-border bg-card px-1 py-0.5 text-[8px]">
                                    <div className="h-0.5 w-full rounded bg-muted-foreground/30 km-skel" style={{ animationDelay: `${-0.5 * i}s` }} />
                                    <div className="mt-0.5 text-violet-500 font-semibold tabular-nums">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    if (kind === 'subscribers') {
        const rows = [
            { email: 'maya@studio.io', score: 92, tag: 'Power', tone: 'bg-success', country: 'US' },
            { email: 'daniel@northbound.dev', score: 87, tag: 'VIP', tone: 'bg-success', country: 'GB' },
            { email: 'priya@bloom.co', score: 71, tag: 'Trial', tone: 'bg-success/70', country: 'IN' },
            { email: 'alex@acme.com', score: 58, tag: 'Cold', tone: 'bg-warning', country: 'DE' },
            { email: 'sam@example.com', score: 34, tag: 'Risk', tone: 'bg-destructive/70', country: 'SG' },
        ];
        return (
            <MockChrome url={url}>
                <div className="grid grid-cols-[80px_1fr] gap-3">
                    {/* Segments rail */}
                    <div className="space-y-1">
                        <div className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.subs.segments')}</div>
                        {[
                            t('landing.mock.subs.seg_all'),
                            t('landing.mock.subs.seg_active30'),
                            t('landing.mock.subs.seg_sleepers'),
                            t('landing.mock.subs.seg_power_users'),
                        ].map((s, i) => (
                            <div
                                key={s}
                                className={cn(
                                    'rounded px-1.5 py-1 text-[9px]',
                                    i === 1 ? 'bg-accent/10 text-accent font-semibold' : 'text-muted-foreground',
                                )}
                            >
                                <div className="truncate">{s}</div>
                                <div className="text-[8px] tabular-nums opacity-70">{[12481, 8420, 1240, 1842][i].toLocaleString()}</div>
                            </div>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-1">
                            {[
                                t('landing.mock.subs.col_status'),
                                t('landing.mock.subs.col_country'),
                                t('landing.mock.subs.col_score'),
                                t('landing.mock.subs.col_tag'),
                                t('landing.mock.subs.col_joined'),
                            ].map((c) => (
                                <span key={c} className="rounded-full border border-border bg-background px-1.5 py-0.5 text-[8px] text-muted-foreground">{c}</span>
                            ))}
                            <span className="ml-auto inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[8px] font-semibold text-accent-foreground km-cta-pulse">{t('landing.mock.subs.add_btn')}</span>
                        </div>
                        <div className="space-y-1">
                            {rows.map((row, i) => (
                                <div
                                    key={row.email}
                                    className="flex items-center gap-2 rounded border border-border bg-background px-1.5 py-1"
                                >
                                    <span className="size-4 shrink-0 rounded-full bg-gradient-to-br from-accent/40 to-accent/10 km-glow" style={{ animationDelay: `${-0.6 * i}s` }} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1">
                                            <p className="truncate font-mono text-[9px]">{row.email}</p>
                                            <span className="rounded bg-muted px-1 text-[8px] uppercase text-muted-foreground">{row.country}</span>
                                        </div>
                                        <div className="relative mt-0.5 h-0.5 w-full overflow-hidden rounded-full bg-muted">
                                            <div className={cn('h-full rounded-full', row.tone)} style={{ width: `${row.score}%` }} />
                                            <div className="pointer-events-none absolute inset-0 km-bar-shimmer text-white/40" style={{ animationDelay: `${-0.4 * i}s` }} />
                                        </div>
                                    </div>
                                    <span className="rounded-full bg-accent/10 px-1 py-0 text-[8px] font-semibold text-accent">{row.tag}</span>
                                    <span className="text-[9px] font-semibold tabular-nums text-foreground">{row.score}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between text-[8px] text-muted-foreground">
                            <span>{t('landing.mock.subs.showing')} <span className="font-semibold text-foreground">1–5</span> {t('landing.mock.subs.of')} <span className="km-digit-tick font-semibold tabular-nums text-foreground">12,481</span></span>
                            <span className="flex gap-1">
                                <span className="rounded border border-border bg-background px-1">‹</span>
                                <span className="rounded border border-accent bg-accent text-accent-foreground px-1 font-semibold">1</span>
                                <span className="rounded border border-border bg-background px-1">2</span>
                                <span className="rounded border border-border bg-background px-1">›</span>
                            </span>
                        </div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    if (kind === 'automation') {
        return (
            <MockChrome url={url}>
                <div className="grid grid-cols-[1fr_104px] gap-2">
                    {/* Flow canvas */}
                    <div className="relative h-[200px] overflow-hidden rounded border border-border bg-[radial-gradient(circle_at_1px_1px,hsl(0_0%_60%/0.18)_1px,transparent_0)] [background-size:14px_14px]">
                        <svg viewBox="0 0 320 200" className="absolute inset-0 size-full" fill="none">
                            <path d="M64 38 C 88 38 96 38 120 38" className="stroke-accent km-arc-draw" strokeWidth="1.5" markerEnd="url(#arr)" />
                            <path d="M184 38 C 208 38 216 38 240 38" className="stroke-accent km-arc-draw" strokeWidth="1.5" markerEnd="url(#arr)" style={{ animationDelay: '-0.8s' }} />
                            <path d="M272 60 C 272 76 224 88 168 96 C 152 100 144 108 144 124" className="stroke-muted-foreground/70 km-arc-draw" strokeWidth="1.5" markerEnd="url(#arr)" fill="none" style={{ animationDelay: '-1.6s' }} />
                            <path d="M184 138 C 208 138 232 144 248 154" className="stroke-success/70 km-arc-draw" strokeWidth="1.5" markerEnd="url(#arr)" fill="none" style={{ animationDelay: '-2.4s' }} />
                            <defs>
                                <marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                                    <path d="M0,0 L0,6 L5,3 z" className="fill-current" />
                                </marker>
                            </defs>
                        </svg>
                        <NodeCard x={6}   y={20}  tone="accent"  Icon={Users}     title={t('welcome.automation.signup', 'Signup')} meta={t('welcome.automation.trigger', 'Trigger')}           pulse />
                        <NodeCard x={122} y={20}  tone="muted"   Icon={Clock}     title={t('welcome.automation.wait_1d', 'Wait 1d')} meta={t('welcome.automation.delay', 'Delay')} />
                        <NodeCard x={242} y={20}  tone="muted"   Icon={Mail}      title={t('welcome.automation.send_mail', 'Send mail')} meta={t('welcome.automation.welcome_series', 'Welcome series')} />
                        <NodeCard x={122} y={112} tone="muted"   Icon={Filter}    title={t('welcome.automation.if_opened', 'If opened')} meta={t('welcome.automation.condition', 'Condition')} />
                        <NodeCard x={242} y={140} tone="success" Icon={Check}     title={t('welcome.automation.tag', 'Tag')} meta={t('welcome.automation.vip', 'VIP')} />
                        <span className="absolute bottom-1.5 left-1.5 rounded-full bg-card/80 px-1.5 py-0.5 text-[8px] backdrop-blur-sm">
                            <span className="font-semibold tabular-nums text-foreground km-digit-tick">1,248</span>
                            <span className="text-muted-foreground"> entered</span>
                        </span>
                    </div>

                    {/* Inspector rail */}
                    <div className="space-y-1.5 text-[8px]">
                        <div className="rounded border border-accent/30 bg-accent/5 px-1.5 py-1">
                            <div className="flex items-center gap-1">
                                <Users className="size-2.5 text-accent" />
                                <span className="font-semibold text-accent">{t('common.page.welcome.mock_trigger', 'Trigger')}</span>
                            </div>
                            <div className="mt-0.5 text-muted-foreground">on subscriber added</div>
                        </div>
                        <div className="space-y-0.5 rounded border border-border bg-background px-1.5 py-1">
                            <p className="font-semibold uppercase tracking-wider text-muted-foreground">{t('common.label.stats', 'Stats')}</p>
                            <div className="flex justify-between"><span>{t('common.page.welcome.mock_entered', 'Entered')}</span><span className="font-semibold tabular-nums">1,248</span></div>
                            <div className="flex justify-between"><span>{t('common.page.welcome.mock_completed', 'Completed')}</span><span className="font-semibold tabular-nums">1,102</span></div>
                            <div className="flex justify-between"><span>{t('common.page.welcome.mock_active', 'Active')}</span><span className="font-semibold tabular-nums">146</span></div>
                        </div>
                        <div className="rounded bg-success/10 px-1.5 py-0.5 text-success font-semibold">
                            <span className="inline-flex size-1 rounded-full bg-success mr-0.5 align-middle" />Active · v3
                        </div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    if (kind === 'landing') {
        return (
            <MockChrome url={url}>
                <div className="grid grid-cols-[78px_1fr_88px] gap-2">
                    {/* Section rail */}
                    <div className="space-y-1 text-[8px]">
                        <div className="font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.landing_card.sections')}</div>
                        {[
                            { name: t('landing.mock.landing_card.sec_hero'),         active: true },
                            { name: t('landing.mock.landing_card.sec_form'),         active: false },
                            { name: t('landing.mock.landing_card.sec_benefits'),     active: false },
                            { name: t('landing.mock.landing_card.sec_testimonials'), active: false },
                            { name: t('landing.mock.landing_card.sec_faq'),          active: false },
                            { name: t('landing.mock.landing_card.sec_footer'),       active: false },
                        ].map((b) => (
                            <div
                                key={b.name}
                                className={cn(
                                    'flex items-center gap-1 rounded border px-1 py-1',
                                    b.active ? 'border-accent/40 bg-accent/5 text-accent font-semibold' : 'border-border bg-background text-muted-foreground',
                                )}
                            >
                                <span className="size-1 rounded-full bg-current opacity-50" />
                                <span className="truncate">{b.name}</span>
                            </div>
                        ))}
                    </div>

                    {/* Live preview */}
                    <div className="relative rounded-md border border-border bg-gradient-to-br from-accent/8 via-accent/3 to-background p-3 text-center">
                        <div className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-success/15 px-1.5 py-0.5 text-[8px] font-semibold text-success">
                            <span className="size-1 rounded-full bg-success" />{t('landing.mock.landing_card.live')}
                        </div>
                        <div className="mx-auto mt-3 h-2 w-32 rounded bg-foreground/80 km-skel" />
                        <div className="mx-auto mt-1.5 h-1.5 w-44 rounded bg-muted-foreground/50 km-skel" style={{ animationDelay: '-0.6s' }} />
                        <div className="mx-auto mt-1 h-1.5 w-36 rounded bg-muted-foreground/40 km-skel" style={{ animationDelay: '-1.2s' }} />
                        <div className="mt-3 space-y-1.5">
                            <div className="mx-auto h-5 w-full rounded bg-background ring-1 ring-border km-glow">
                                <div className="h-full w-1/2 rounded-l bg-accent/12" />
                            </div>
                            <div className="mx-auto h-5 w-full rounded bg-background ring-1 ring-border km-glow" style={{ animationDelay: '-1.2s' }} />
                        </div>
                        <div className="mx-auto mt-2.5 h-5 w-20 rounded bg-accent km-cta-pulse" />
                        <div className="mt-2 flex flex-wrap items-center justify-center gap-1 text-[8px] text-muted-foreground">
                            <span>{t('landing.mock.landing_card.views')} <span className="font-semibold tabular-nums text-foreground km-digit-tick">4,128</span></span>
                            <span>·</span>
                            <span>{t('landing.mock.landing_card.conv')} <span className="font-semibold tabular-nums text-foreground km-digit-tick">14.8%</span></span>
                        </div>
                    </div>

                    {/* Properties */}
                    <div className="space-y-1.5 text-[8px]">
                        <div className="font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.landing_card.style')}</div>
                        <div className="rounded border border-border bg-background px-1.5 py-1">
                            <p className="text-muted-foreground">{t('landing.mock.landing_card.accent')}</p>
                            <div className="mt-0.5 flex gap-0.5">
                                {['bg-accent', 'bg-violet-500', 'bg-emerald-500', 'bg-blue-500'].map((c, i) => (
                                    <span key={c} className={cn('size-2.5 rounded-sm', c, i === 0 && 'ring-1 ring-foreground/40')} />
                                ))}
                            </div>
                        </div>
                        <div className="rounded border border-border bg-background px-1.5 py-1">
                            <p className="text-muted-foreground">{t('landing.mock.landing_card.slug')}</p>
                            <p className="mt-0.5 font-mono text-foreground/80 truncate">{t('landing.mock.landing_card.slug_value')}</p>
                        </div>
                        <div className="rounded bg-accent/10 px-1.5 py-0.5 text-accent font-semibold km-cta-pulse">{t('landing.mock.landing_card.publish')}</div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    if (kind === 'integrations') {
        const ic = (k) => t(`landing.mock.integrations.${k}`);
        const cards = [
            { Ic: ShoppingBag, product: 'Shopify',     status: ic('connected'),    when: '24m', tone: 'success' },
            { Ic: ShoppingBag, product: 'WooCommerce', status: ic('connected'),    when: '1h',  tone: 'success' },
            { Ic: Webhook,     product: 'Stripe',      status: ic('connected'),    when: '4m',  tone: 'success' },
            { Ic: Plug,        product: 'Webhooks',    status: ic('three_active'), when: 'live', tone: 'success' },
            { Ic: Code,        product: 'REST API',    status: ic('token_set'),    when: '—',   tone: 'accent' },
            { Ic: Zap,         product: 'Zapier',      status: ic('connect'),      when: '—',   tone: 'muted' },
        ];
        return (
            <MockChrome url={url}>
                <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-1.5">
                        {cards.map(({ Ic, product, status, when, tone }, i) => (
                            <div
                                key={product}
                                className="rounded-md border border-border bg-background px-2 py-1.5"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="grid size-5 place-items-center rounded bg-accent/10 text-accent">
                                        <Ic className="size-3" strokeWidth={1.8} />
                                    </span>
                                    {tone === 'success' && (
                                        <span className="relative inline-flex size-1.5 rounded-full bg-success pulse-dot text-success" />
                                    )}
                                </div>
                                <p className="mt-1 text-[9px] font-semibold text-foreground">{product}</p>
                                <div className="mt-0.5 flex items-center justify-between text-[8px]">
                                    <span className={cn(
                                        tone === 'success' && 'text-success',
                                        tone === 'accent' && 'text-accent',
                                        tone === 'muted' && 'text-muted-foreground',
                                    )}>{status}</span>
                                    <span className="text-muted-foreground tabular-nums">{when}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-md border border-border bg-background p-1.5">
                        <p className="mb-1 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.integrations.activity')}</p>
                        <div className="space-y-0.5 font-mono text-[8px] leading-tight text-muted-foreground">
                            {[
                                { code: 'POST', path: '/api/v1/subscribers', status: '201', time: '14ms', live: true },
                                { code: 'POST', path: '/webhooks/stripe.invoice.paid', status: '200', time: '38ms', live: false },
                                { code: 'GET', path: '/api/v1/lists/1', status: '200', time: '9ms', live: false },
                            ].map((row, i) => (
                                <div key={i} className="flex items-center gap-1.5">
                                    <span className="text-success km-glow" style={{ animationDelay: `${-0.6 * i}s` }}>{row.code}</span>
                                    <span className="truncate flex-1 text-foreground/70">{row.path}</span>
                                    <span className="text-accent tabular-nums">{row.status}</span>
                                    <span className="tabular-nums">{row.time}</span>
                                    {row.live && <span className="inline-block h-1.5 w-px bg-accent km-cursor-blink" />}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    if (kind === 'analytics') {
        return (
            <MockChrome url={url}>
                <div className="space-y-2">
                    {/* Tabs */}
                    <div className="flex items-center gap-2 border-b border-border pb-1 text-[9px]">
                        {[
                            { l: t('landing.mock.analytics.tab_overview'),       active: true },
                            { l: t('landing.mock.analytics.tab_engagement'),     active: false },
                            { l: t('landing.mock.analytics.tab_deliverability'), active: false },
                            { l: t('landing.mock.analytics.tab_top_urls'),       active: false },
                            { l: t('landing.mock.analytics.tab_geo'),            active: false },
                        ].map((tab) => (
                            <span
                                key={tab.l}
                                className={cn(
                                    'pb-1',
                                    tab.active ? 'border-b-2 border-accent text-foreground font-semibold' : 'text-muted-foreground',
                                )}
                            >
                                {tab.l}
                            </span>
                        ))}
                        <span className="ml-auto rounded bg-secondary/40 px-1 py-0.5 text-[8px] text-muted-foreground">{t('landing.mock.analytics.last_30d')}</span>
                    </div>

                    {/* KPI tiles */}
                    <div className="grid lg:grid-cols-4 grid-cols-2 gap-1">
                        {[
                            { l: t('landing.mock.analytics.kpi_sent'),    v: '128K',  tone: 'text-foreground', delta: '+4%' },
                            { l: t('landing.mock.analytics.kpi_opens'),   v: '54.2%', tone: 'text-success',    delta: '+12%' },
                            { l: t('landing.mock.analytics.kpi_clicks'),  v: '12.8%', tone: 'text-accent',     delta: '+3%' },
                            { l: t('landing.mock.analytics.kpi_bounces'), v: '0.4%',  tone: 'text-warning',    delta: '−0.1%' },
                        ].map((s, i) => (
                            <div key={s.l} className="rounded-md border border-border bg-background p-1.5">
                                <div className="flex items-baseline justify-between">
                                    <p className="text-[8px] uppercase tracking-wider text-muted-foreground">{s.l}</p>
                                    <p className="text-[8px] font-semibold tabular-nums text-success">{s.delta}</p>
                                </div>
                                <p className={cn('mt-0.5 text-[14px] font-bold leading-none tabular-nums km-digit-tick', s.tone)} style={{ animationDelay: `${-0.5 * i}s` }}>{s.v}</p>
                                {/* tile sparkline */}
                                <svg viewBox="0 0 60 14" className="mt-1 w-full" fill="none">
                                    <path d="M0 10 L10 8 L20 9 L30 5 L40 7 L50 3 L60 4" className={cn('stroke-current km-spark-draw', s.tone)} strokeWidth="1.2" />
                                </svg>
                            </div>
                        ))}
                    </div>

                    {/* Main chart + Top URLs */}
                    <div className="grid grid-cols-[1fr_92px] gap-2">
                        <div className="rounded-md border border-border bg-background p-1.5">
                            <div className="mb-0.5 flex items-center justify-between text-[8px]">
                                <p className="font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.analytics.chart_title')}</p>
                                <span className="text-success tabular-nums">{t('landing.mock.analytics.chart_meta', null, { open: '54.2%', click: '12.8%' })}</span>
                            </div>
                            <svg viewBox="0 0 320 80" className="w-full" fill="none">
                                <defs>
                                    <linearGradient id="grad-acc-rep" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.4" />
                                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                {/* Opens area + line */}
                                <path d="M0 60 L40 45 L80 50 L120 30 L160 40 L200 22 L240 28 L280 12 L320 18 L320 80 L0 80 Z" className="fill-accent/30" fill="url(#grad-acc-rep)" />
                                <path d="M0 60 L40 45 L80 50 L120 30 L160 40 L200 22 L240 28 L280 12 L320 18" className="stroke-accent km-spark-draw" strokeWidth="1.6" />
                                {/* Clicks line — secondary tone */}
                                <path d="M0 72 L40 68 L80 70 L120 58 L160 64 L200 52 L240 56 L280 44 L320 48" className="stroke-violet-500/80 km-spark-draw" strokeWidth="1.4" style={{ animationDelay: '-1.5s' }} />
                                {/* axis baseline */}
                                <line x1="0" y1="78" x2="320" y2="78" className="stroke-border" strokeWidth="0.6" />
                            </svg>
                        </div>
                        <div className="rounded-md border border-border bg-background p-1.5 text-[8px]">
                            <p className="mb-1 font-semibold uppercase tracking-wider text-muted-foreground">{t('landing.mock.analytics.top_urls')}</p>
                            <div className="space-y-1">
                                {[
                                    { p: '/spring-sale', c: '4,128' },
                                    { p: '/pricing', c: '2,840' },
                                    { p: '/dashboard', c: '1,612' },
                                    { p: '/blog/launch', c: '892' },
                                ].map((u, i) => (
                                    <div key={u.p} className="flex items-center gap-1">
                                        <span className="size-1 rounded-full bg-accent" />
                                        <span className="truncate flex-1 font-mono text-foreground/80">{u.p}</span>
                                        <span className="tabular-nums">{u.c}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </MockChrome>
        );
    }

    return null;
}

function NodeCard({ x, y, tone = 'muted', Icon, title, meta, pulse = false }) {
    const palette = {
        accent: { bg: 'bg-accent/15', border: 'border-accent/50', text: 'text-accent' },
        success: { bg: 'bg-success/15', border: 'border-success/50', text: 'text-success' },
        muted: { bg: 'bg-card', border: 'border-border', text: 'text-foreground' },
    }[tone];
    return (
        <div
            className={cn(
                'absolute w-[60px] rounded-md border px-1 py-0.5 shadow-sm',
                palette.bg,
                palette.border,
                pulse && 'km-node-pulse',
            )}
            style={{ left: x, top: y }}
        >
            <div className={cn('flex items-center gap-0.5 text-[8px] font-semibold', palette.text)}>
                {Icon && <Icon className="size-2.5" strokeWidth={2.4} />}
                <span className="truncate">{title}</span>
            </div>
            <div className="text-[7px] text-muted-foreground truncate">{meta}</div>
        </div>
    );
}

/* ---------- page ---------- */

export default function Welcome({ branding = {}, landing = {}, plans = [], trial_days_default = 0, billing_enabled = true }) {
    const { auth, theme, auth_config } = usePage().props;
    const { t } = useT();
    const adminAuthed = Boolean(auth?.admin);
    const customerAuthed = Boolean(auth?.customer);
    const isAuthed = adminAuthed || customerAuthed;
    const dashboardHref = adminAuthed ? '/admin/dashboard' : '/dashboard';
    // Start-free CTA → /register when signup is enabled, /login otherwise.
    // Sign-in CTA always → /login. Decouples the two buttons so they don't
    // both land on the same screen — reviewer-grade fix.
    const signupHref = auth_config?.allow_self_signup ? '/register' : '/login';
    const ctaHref = isAuthed ? dashboardHref : signupHref;
    // Local dark flag; preserveState keeps shared prop stale, so we mirror it.
    const [isDark, setIsDark] = useState(theme?.mode === 'dark');
    const toggleTheme = () => {
        const next = !isDark;
        setIsDark(next);
        if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', next);
        }
        router.post('/_theme/toggle', {}, {
            preserveScroll: true,
            preserveState: true,
            onError: () => {
                setIsDark(!next);
                if (typeof document !== 'undefined') {
                    document.documentElement.classList.toggle('dark', !next);
                }
            },
        });
    };
    const [tourVideoOpen, setTourVideoOpen] = useState(false);
    const tourVideoUrl = (landing.hero_tour_video_url || '').trim();
    const hasTourVideo = tourVideoUrl.length > 0;

    const siteName = branding.site_name || t('landing.brand.default_name', 'Email service');
    const tagline = branding.site_tagline || t('landing.brand.default_tagline', 'Send better email. Grow your business.');
    const copyright = branding.copyright || `© ${siteName}. ${t('landing.brand.rights_reserved', 'All rights reserved.')}`;

    const primaryCta = isAuthed ? t('landing.cta.open_dashboard', 'Open dashboard') : t('landing.cta.start_free', 'Start free');
    const heroPrimary = isAuthed
        ? t('landing.cta.open_dashboard', 'Open dashboard')
        : (landing.hero_cta_primary && landing.hero_cta_primary !== 'Get started free'
            ? landing.hero_cta_primary
            : (trial_days_default > 0 ? t('landing.cta.start_trial_days', `Start ${trial_days_default}-day free trial`, { days: trial_days_default }) : (landing.hero_cta_primary || t('landing.cta.get_started_free', 'Get started free'))));

    const ctaTagline = landing.hero_title || tagline;
    const stats = [
        { value: landing.stat_1_value || '99.97%', label: landing.stat_1_label || t('landing.stats.inbox_placement', 'Inbox placement') },
        { value: landing.stat_2_value || '5M+', label: landing.stat_2_label || t('landing.stats.monthly_sends', 'Monthly sends') },
        { value: landing.stat_3_value || '<200ms', label: landing.stat_3_label || t('landing.stats.api_response', 'API response') },
        { value: landing.stat_4_value || '24/7', label: landing.stat_4_label || t('landing.stats.in_app_support', 'In-app support') },
    ];

    // Section visibility — default true.
    const show = {
        stats: landing.show_stats !== false,
        features: landing.show_features !== false,
        product: landing.show_product !== false,
        integrations: landing.show_integrations !== false,
        testimonials: landing.show_testimonials !== false,
        pricing: landing.show_pricing !== false && plans.length > 0 && billing_enabled,
        faq: landing.show_faq !== false,
        cta: landing.show_cta !== false,
        newsletter: landing.show_newsletter !== false,
    };

    // Admin list-overrides; fall back to translated seed defaults.
    const featuresList = (Array.isArray(landing.features_list) && landing.features_list.length
        ? landing.features_list.map((it) => ({ ...it, icon: lucide(it.icon, Send) }))
        : defaultFeatures(t));
    const integrationsList = (Array.isArray(landing.integrations_list) && landing.integrations_list.length
        ? landing.integrations_list.map((it) => ({ ...it, icon: lucide(it.icon, Plug) }))
        : defaultIntegrations(t));
    const testimonialsList = (Array.isArray(landing.testimonials_list) && landing.testimonials_list.length
        ? landing.testimonials_list
        : defaultTestimonials(t));
    const faqsList = (Array.isArray(landing.faqs_list) && landing.faqs_list.length
        ? landing.faqs_list
        : defaultFaqs(t));
    const showcasesList = (Array.isArray(landing.showcases_list) && landing.showcases_list.length
        ? landing.showcases_list.map((s) => ({
            ...s,
            icon: lucide(s.icon, Send),
            bullets: typeof s.bullets === 'string'
                ? s.bullets.split('\n').map((b) => b.trim()).filter(Boolean)
                : (Array.isArray(s.bullets) ? s.bullets : []),
        }))
        : defaultShowcases(t));

    // Hero rotator + ticker from admin lists.
    const adminRotator = Array.isArray(landing.hero_rotating_words)
        ? landing.hero_rotating_words.map((it) => (it?.word ?? '').trim()).filter(Boolean)
        : [];
    const rotatorWords = adminRotator.length
        ? [ctaTagline.split(' ').slice(-2).join(' '), ...adminRotator]
        : [ctaTagline.split(' ').slice(-2).join(' ')];

    const aiFeedItems = (Array.isArray(landing.hero_ai_feed) ? landing.hero_ai_feed : [])
        .map((it) => ({ label: (it?.label ?? '').trim(), meta: (it?.meta ?? '').trim() }))
        .filter((it) => it.label);

    const trustMicrocopy = (landing.hero_trust_microcopy ?? t('landing.hero.trust_microcopy', 'No credit card · Cancel any time')).trim();
    const footerProductLinks = Array.isArray(landing.footer_product_links) && landing.footer_product_links.length
        ? landing.footer_product_links
        : null;
    const footerResourcesLinks = Array.isArray(landing.footer_resources_links) && landing.footer_resources_links.length
        ? landing.footer_resources_links
        : null;
    const footerCompanyLinks = Array.isArray(landing.footer_company_links) && landing.footer_company_links.length
        ? landing.footer_company_links
        : null;
    const footerLegalLinks = Array.isArray(landing.footer_legal_links) && landing.footer_legal_links.length
        ? landing.footer_legal_links
        : null;
    const footerSocialLinks = Array.isArray(landing.footer_social_links) && landing.footer_social_links.length
        ? landing.footer_social_links
        : null;



    const [open, setOpen] = useState(false);

    const navItems = [
        { href: '#features', label: t('landing.nav.features', 'Features') },
        { href: '#product', label: t('landing.nav.product_tour', 'Product tour') },
        { href: '#integrations', label: t('landing.nav.integrations', 'Integrations') },
        ...(plans.length > 0 && billing_enabled
            ? [{ href: '#pricing', label: t('landing.nav.pricing', 'Pricing') }]
            : []),
        { href: '#faq', label: t('landing.nav.faq', 'FAQ') },
    ];

    return (
        <>
            <Head title={`${siteName} — ${tagline}`} />


            <div className="min-h-screen bg-background ">
                {/* ===== NAV ===== */}
                <header className="sticky top-0 z-30 bg-gradient-to-b from-accent/30 to-transparent backdrop-blur-md px-2 pt-2">
                    <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 lg:rounded-4xl rounded-xl bg-card px-3 shadow-custom sm:h-16 sm:px-5">

                        <BrandLogo
                            name={siteName}
                            logo={branding.site_logo}
                        />

                        {/* Desktop Navigation */}
                        <nav className="hidden items-center gap-1 text-sm font-semibold md:flex">
                            {navItems.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="group relative rounded-md px-3 py-1.5 text-foreground/80 transition-colors duration-200 hover:text-accent"
                                >
                                    <span className="relative z-[1]">
                                        {item.label}
                                    </span>

                                    <span
                                        aria-hidden
                                        className="pointer-events-none absolute inset-0 rounded-md bg-accent/0 transition-colors duration-200 group-hover:bg-accent/10"
                                    />

                                    <span
                                        aria-hidden
                                        className="pointer-events-none absolute -bottom-0.5 left-1/2 h-[2px] w-0 -translate-x-1/2 rounded-full bg-gradient-to-r from-accent via-violet-500 to-purple-500 transition-[width] duration-300 ease-out group-hover:w-[60%]"
                                    />
                                </a>
                            ))}
                        </nav>

                        {/* Right Side */}
                        <div className="flex shrink-0 items-center gap-2">

                            {/* Desktop Controls */}
                            <div className="hidden items-center gap-1.5 md:flex">
                                <LocaleSwitcher />

                                <button
                                    type="button"
                                    onClick={toggleTheme}
                                    aria-label={t('common.label.toggle_theme', 'Toggle theme')}
                                    title={t('common.label.toggle_theme', 'Toggle theme')}
                                    className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-card text-foreground transition-all hover:border-accent/40 hover:text-accent"
                                >
                                    {isDark ? (
                                        <Sun className="size-3.5" />
                                    ) : (
                                        <Moon className="size-3.5" />
                                    )}
                                </button>

                                <span className="mx-1 h-5 w-px bg-border" />

                                {!isAuthed && (
                                    <Link href="/login">
                                        <Button variant="ghost" size="sm">
                                            {t('landing.nav.sign_in', 'Sign in')}
                                        </Button>
                                    </Link>
                                )}
                            </div>

                            {/* CTA */}
                            <Link href={ctaHref}>
                                <Button variant="accent" size="sm" className="px-3 sm:px-4">
                                    <span className="hidden sm:inline">
                                        {primaryCta}
                                    </span>

                                    <span className="sm:hidden">
                                        {isAuthed
                                            ? t('landing.cta.open_dashboard', 'Dashboard')
                                            : t('landing.cta.start_free', 'Start free')}
                                    </span>

                                    <ArrowRight className="size-4 hidden lg:inline-block" />
                                </Button>
                            </Link>

                            {/* Mobile Menu Button */}
                            <button
                                type="button"
                                onClick={() => setOpen(!open)}
                                className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-card transition hover:border-accent/40 hover:text-accent md:hidden"
                            >
                                {open ? (
                                    <LucideIcons.X className="size-4" />
                                ) : (
                                    <LucideIcons.Menu className="size-4" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {open && (
                        <div className="mx-auto mt-2 max-w-7xl md:hidden">
                            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-custom">

                                <nav className="p-2">
                                    {navItems.map((item) => (
                                        <a
                                            key={item.href}
                                            href={item.href}
                                            onClick={() => setOpen(false)}
                                            className="flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium text-foreground transition hover:bg-accent/5 hover:text-accent"
                                        >
                                            <span>{item.label}</span>
                                            <ArrowRight className="size-4 opacity-40" />
                                        </a>
                                    ))}
                                </nav>

                                <div className="mx-4 border-t border-border" />

                                <div className="space-y-3 p-4">

                                    <LocaleSwitcher />

                                    <button
                                        type="button"
                                        onClick={toggleTheme}
                                        className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-medium transition hover:border-accent/40 hover:text-accent"
                                    >
                                        <span>
                                            {t('common.label.toggle_theme', 'Toggle Theme')}
                                        </span>

                                        {isDark ? (
                                            <Sun className="size-4" />
                                        ) : (
                                            <Moon className="size-4" />
                                        )}
                                    </button>

                                    {!isAuthed && (
                                        <Link
                                            href="/login"
                                            onClick={() => setOpen(false)}
                                        >
                                            <Button
                                                variant="ghost"
                                                className="w-full"
                                            >
                                                {t('landing.nav.sign_in', 'Sign in')}
                                            </Button>
                                        </Link>
                                    )}
                                </div>

                            </div>
                        </div>
                    )}
                </header>

                {/* ===== HERO ===== */}
                <section className="relative overflow-hidden">
                    <HeroBackdrop />
                    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24 relative z-[2]">
                        <Reveal>
                            <div className="mx-auto max-w-4xl text-center">
                                {/* Eyebrow: shine + ring + halo. */}
                                <div className="km-pill-shine km-pill-ring mx-auto inline-flex items-center gap-1.5 rounded-full border border-accent/35 bg-card/60 p-1 mb-6 backdrop-blur-sm">
                                    <div className="relative grid size-5 lg:size-7 place-items-center rounded-full bg-gradient-to-br from-accent to-violet-500">
                                        <Sparkles className="size-3 text-white" />
                                        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-accent/40 km-glow" />
                                    </div>
                                    <div className="pe-3 lg:text-[13px] text-[12px] font-medium leading-none">
                                        {landing.hero_eyebrow}
                                    </div>
                                </div>

                                <h1 className="text-balance break-words font-semibold tracking-tight leading-[1.08] pb-1 text-[2.25rem] sm:text-5xl lg:text-6xl">
                                    {ctaTagline.split(' ').slice(0, -2).join(' ')}{' '}
                                    <RotatingWord words={rotatorWords} interval={2800} />
                                </h1>
                                <p className="mx-auto mt-6 max-w-3xl text-balance text-base text-muted-foreground sm:text-lg">
                                    {landing.hero_subheadline}
                                </p>

                                {aiFeedItems.length > 0 && (
                                    <AILiveFeed interval={3600} items={aiFeedItems} />
                                )}
                                <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                                    <Link href={ctaHref}>
                                        <Button variant="accent" size="lg">
                                            {heroPrimary}
                                            <ArrowRight className="size-4" />
                                        </Button>
                                    </Link>
                                    {hasTourVideo ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="lg"
                                            onClick={() => setTourVideoOpen(true)}
                                        >
                                            <PlayCircle className="size-4" />
                                            {landing.hero_cta_secondary || t('landing.hero.take_product_tour', 'Take product tour')}
                                        </Button>
                                    ) : show.product && (
                                        <a href="#product">
                                            <Button variant="outline" size="lg">
                                                <PlayCircle className="size-4" />
                                                {landing.hero_cta_secondary || t('landing.hero.take_product_tour', 'Take product tour')}
                                            </Button>
                                        </a>
                                    )}
                                </div>
                                {!isAuthed && (
                                    <p className="mt-5 inline-flex items-center gap-2 text-xs text-muted-foreground">
                                        <span className="relative inline-flex size-2 text-success pulse-dot"><span className="size-2 rounded-full bg-success" /></span>
                                        {trial_days_default > 0
                                            ? `${trial_days_default}${t('landing.hero.trial_days_suffix', '-day free trial · ')}${trustMicrocopy}`
                                            : trustMicrocopy}
                                    </p>
                                )}
                            </div>
                        </Reveal>

                        {/* Hero mock — resolution order:
                              1. `hero_image_url` (single static override) wins if set
                              2. `hero_showcase_enabled` + slides → animated showcase
                              3. Built-in SVG mock fallback */}
                        <Reveal delay={300} className="mx-auto mt-12 max-w-5xl relative z-[2] sm:mt-20">
                            {landing.hero_image_url ? (
                                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-accent/10 ring-1 ring-accent/10">
                                    <img
                                        src={landing.hero_image_url}
                                        alt={ctaTagline}
                                        className="block h-auto w-full"
                                    />
                                </div>
                            ) : landing.hero_showcase_enabled && Array.isArray(landing.hero_showcase_slides) && landing.hero_showcase_slides.length > 0 ? (
                                <HeroShowcase landing={landing} />
                            ) : (
                                <Mock kind={landing.hero_mock_kind || 'campaign'} url={landing.hero_showcase_chrome_url} />
                            )}
                        </Reveal>
                    </div>
                </section>

                {/* ===== SOCIAL PROOF STRIP ===== */}
                {show.stats && (
                    <section className="relative overflow-hidden border-y border-border/40 bg-background">
                        {/* Ambient backdrop — coral + violet radial glows tracked
                        through token colours so the strip flips cleanly in
                        dark mode. */}
                        <div aria-hidden className="pointer-events-none absolute inset-0 -z-[1]">
                            <div className="absolute inset-0 bg-gradient-to-b from-card/40 via-background to-background" />
                            <div className="absolute -top-32 left-[15%] size-[480px] rounded-full bg-accent/8 blur-3xl" />
                            <div className="absolute -bottom-32 right-[12%] size-[440px] rounded-full bg-violet-500/8 blur-3xl" />
                        </div>

                        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20">
                            <Reveal>
                                <div className="flex flex-wrap items-center justify-center gap-3 text-center">
                                    <span className="inline-flex size-1.5 rounded-full bg-accent shadow-[0_0_12px] shadow-accent/60" />
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                                        {landing.social_proof_label || t('landing.social_proof.label', 'Trusted by growing teams worldwide')}
                                    </p>
                                    <span className="inline-flex size-1.5 rounded-full bg-accent shadow-[0_0_12px] shadow-accent/60" />
                                </div>
                            </Reveal>

                            <Reveal delay={120}>
                                <div className="mt-10 grid gap-4 sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
                                    {stats.map((s, i) => {
                                        const Icon = STAT_ICONS[i % STAT_ICONS.length];
                                        const tone = STAT_TONES[i % STAT_TONES.length];
                                        return (
                                            <div
                                                key={s.label}
                                                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-xl hover:shadow-accent/10"
                                            >
                                                <div aria-hidden className={`absolute -right-10 -top-10 size-32 rounded-full ${tone.glow} opacity-60 blur-3xl transition-opacity duration-300 group-hover:opacity-90`} />
                                                <div className="relative flex items-start justify-between">
                                                    <span className={`grid size-9 place-items-center rounded-xl ${tone.bg} ${tone.text}`}>
                                                        <Icon className="size-4" strokeWidth={2} />
                                                    </span>
                                                    {i === 0 && (
                                                        <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/5 px-2 py-0.5 text-[10px] font-semibold text-success">
                                                            <span className="inline-flex size-1 rounded-full bg-success" />
                                                            {t('landing.stats.live_badge', 'Live')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="relative mt-5">
                                                    <p className="text-2xl lg:text-3xl font-semibold tracking-tight tabular-nums text-foreground sm:text-[34px]">
                                                        {s.value}
                                                    </p>
                                                    <p className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                                        {s.label}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Reveal>

                            <Reveal delay={220}>
                                <p className="mt-10 text-center text-xs text-muted-foreground">
                                    {t('landing.stats.footnote', 'Backed by hardened sending infrastructure · DKIM signed on every send · Real-time engagement & deliverability dashboards')}
                                </p>
                            </Reveal>
                        </div>
                    </section>
                )}

                {/* ===== FEATURES OVERVIEW ===== */}
                {show.features && (
                    <section id="features" className="border-b border-border/40">
                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
                            <Reveal>
                                <div className="mx-auto max-w-2xl text-center">
                                    <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                                        <Sparkles className="size-3 text-accent" />
                                        {t('landing.features.eyebrow', 'Everything in one platform')}
                                    </div>
                                    <h2 className="text-balance text-xl font-semibold tracking-tight sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[1.05]">
                                        {landing.features_title || t('landing.features.title', 'Everything you need to scale your email')}
                                    </h2>
                                    <p className="mx-auto mt-2 lg:mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
                                        {landing.features_subtitle || t('landing.features.subtitle', 'A complete email-marketing stack — no patchwork of subscriptions, no deliverability consultant.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className="mt-10 grid gap-4 sm:mt-16 sm:grid-cols-2 lg:grid-cols-3">
                                {featuresList.map((f, i) => (
                                    <Reveal key={f.title || i} delay={i * 60}>
                                        <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-card lg:p-6 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-xl hover:shadow-accent/10">
                                            {/* Hover glow — subtle coral wash from the top-right
                                            corner. Only visible on hover, hardware-accelerated. */}
                                            <div aria-hidden className="pointer-events-none absolute -right-12 -top-12 size-36 rounded-full bg-accent/0 blur-3xl transition-all duration-500 group-hover:bg-accent/25" />

                                            <div className="relative flex lg:flex-row flex-col items-start gap-4">
                                                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-accent to-accent/70 text-accent-foreground shadow-sm shadow-accent/20 ring-1 ring-accent/20 transition-transform duration-300 group-hover:scale-105">
                                                    <f.icon className="size-5" strokeWidth={2} />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="text-[15px] font-semibold tracking-tight text-foreground">{f.title}</h3>
                                                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                                                </div>
                                            </div>

                                            {/* Bottom hairline that lights up on hover. */}
                                            <div aria-hidden className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-accent/0 to-transparent transition-all duration-500 group-hover:via-accent/60" />
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    </section>

                )}

                {/* ===== PRODUCT SHOWCASES (alternating) ===== */}
                {show.product && (
                    <section id="product" className="border-b border-border/40 bg-card">
                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
                            <Reveal>
                                <div className="mx-auto max-w-2xl text-center">
                                    <Badge tone="accent" className="mb-4 gap-1.5">
                                        <PlayCircle className="size-3.5" />
                                        {t('landing.product.badge', 'Product tour')}
                                    </Badge>
                                    <h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                                        {landing.product_title || t('landing.product.title', 'A full marketing platform, one workspace')}
                                    </h2>
                                    <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                                        {landing.product_subtitle || t('landing.product.subtitle', 'Every module designed to work with the next. Click through each, or scroll for the deep dive.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className="mt-20 space-y-24">
                                {showcasesList.map((s, i) => (
                                    <Reveal key={s.title || i}>
                                        <div className={cn(
                                            'grid items-center gap-8 lg:grid-cols-2 lg:gap-12',
                                            i % 2 === 1 && 'lg:[&>div:first-child]:order-2',
                                        )}>
                                            <div className="min-w-0">
                                                <Badge tone="accent" className="mb-4 gap-1.5">
                                                    <s.icon className="size-3.5" />
                                                    {s.eyebrow}
                                                </Badge>
                                                <h3 className="text-balance text-xl font-semibold sm:text-3xl lg:text-4xl">{s.title}</h3>
                                                <p className="mt-4 text-muted-foreground">{s.body}</p>
                                                <ul className="mt-6 space-y-3">
                                                    {(s.bullets ?? []).map((b) => (
                                                        <li key={b} className="flex items-start gap-2.5 text-sm lg:text-base">
                                                            <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/20">
                                                                <Check className="size-3" strokeWidth={3} />
                                                            </span>
                                                            <span>{b}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="min-w-0">
                                                {s.image_url ? (
                                                    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-accent/10 ring-1 ring-accent/10">
                                                        <img
                                                            src={s.image_url}
                                                            alt={s.title || s.eyebrow || t('landing.product.showcase_alt', 'Product showcase')}
                                                            className="block h-auto w-full"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ) : (
                                                    <Mock kind={s.mock} url={landing.hero_showcase_chrome_url} />
                                                )}
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    </section>

                )}

                {/* ===== INTEGRATIONS ===== */}
                {show.integrations && (
                    <section id="integrations" className="border-b border-border/40 bg-secondary/20">
                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
                            <Reveal>
                                <div className="mx-auto max-w-2xl text-center">
                                    <Badge tone="accent" className="mb-4 gap-1.5">
                                        <Plug className="size-3.5" />
                                        {t('landing.integrations.badge', 'Integrations')}
                                    </Badge>
                                    <h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                                        {landing.integrations_title || t('landing.integrations.title', 'Plug into your stack')}
                                    </h2>
                                    <p className="mx-auto lg:mt-4 mt-2 max-w-xl text-muted-foreground">
                                        {landing.integrations_subtitle || t('landing.integrations.subtitle', 'Native integrations for the apps your customers already use. Plus a full REST API and outbound webhooks for everything else.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className="mt-8 grid gap-3 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3">
                                {integrationsList.map((it, i) => (
                                    <Reveal key={it.name || i} delay={i * 60}>
                                        <div className="flex items-center gap-4 rounded-full border border-border bg-card p-3 transition-all hover:border-accent/40 hover:shadow-md">
                                            <span className="grid lg:size-14 size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-accent/15 via-accent/10 to-transparent text-accent ring-1 ring-accent/20">
                                                <it.icon className="lg:size-7 size-5" strokeWidth={1.75} />
                                            </span>
                                            <div className="min-w-0">
                                                <p className="font-semibold lg:text-lg text-sm tracking-tight mb-1">{it.name}</p>
                                                <p className="text-xs text-muted-foreground">{it.hint}</p>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    </section>

                )}

                {/* ===== TESTIMONIALS ===== */}
                {show.testimonials && (
                    <section className="border-b border-border/40 bg-card">
                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
                            <Reveal>
                                <div className="mx-auto max-w-2xl text-center">
                                    <h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                                        {landing.testimonials_title || t('landing.testimonials.title', 'Marketers love it')}
                                    </h2>
                                    <p className="mx-auto lg:mt-4 mt-2 max-w-xl text-muted-foreground">
                                        {landing.testimonials_subtitle || t('landing.testimonials.subtitle', 'Real outcomes from teams that switched.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className="relative lg:mt-16 mt-8">
                                <Swiper
                                    modules={[Navigation, Pagination, Autoplay]}
                                    spaceBetween={20}
                                    slidesPerView={1}
                                    loop={true}
                                    autoplay={{
                                        delay: 5000,
                                        disableOnInteraction: false,
                                    }}
                                    navigation={{
                                        prevEl: '.testimonial-prev',
                                        nextEl: '.testimonial-next',
                                    }}
                                    pagination={{
                                        el: '.testimonial-fraction',
                                        type: 'fraction',
                                    }}
                                    breakpoints={{
                                        768: {
                                            slidesPerView: 2,
                                        },
                                        1280: {
                                            slidesPerView: 2,
                                        },
                                    }}
                                >
                                    {testimonialsList.map((t, i) => (
                                        <SwiperSlide key={i}>
                                            <Reveal delay={i * 100}>
                                                <figure className="h-full relative z-[1] group rounded-2xl transition-all duration-500 border border-border hover:border-accent/40 bg-card p-7 overflow-hidden">
                                                    <div className="absolute opacity-0 group-hover:opacity-100 transition-all duration-500 z-[-1] left-0 top-0 w-full h-full bg-gradient-to-b from-accent/30 to-transparent z-[-1]"></div>
                                                    <div className="flex gap-0.5">
                                                        {Array.from({ length: 5 }).map((_, j) => (
                                                            <Star
                                                                key={j}
                                                                className="size-3 fill-yellow-500 text-yellow-500"
                                                            />
                                                        ))}
                                                    </div>

                                                    <blockquote className="mt-4 text-base lg:text-lg leading-relaxed">
                                                        "{t.quote}"
                                                    </blockquote>

                                                    <figcaption className="mt-5 flex items-center gap-3 border-t border-border pt-4">
                                                        <span className="size-9 rounded-full bg-gradient-to-br from-accent/40 to-accent/10 ring-1 ring-accent/20" />

                                                        <div>
                                                            <p className="text-sm font-semibold">
                                                                {t.author}
                                                            </p>

                                                            <p className="text-xs text-muted-foreground">
                                                                {t.role}
                                                            </p>
                                                        </div>
                                                    </figcaption>
                                                </figure>
                                            </Reveal>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>

                                {/* Navigation */}
                                <div className="relative z-10 flex justify-center items-center max-w-[120px] mx-auto gap-3 mt-5">
                                    <button className="testimonial-prev flex shrink-0 h-11 w-11 items-center justify-center rounded-xl border border-border bg-card transition hover:bg-accent/5">
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <div className="testimonial-fraction ml-2 mx-3 inline-block text-sm font-medium text-muted-foreground" />

                                    <button className="testimonial-next flex shrink-0 h-11 w-11 items-center justify-center rounded-xl border border-border bg-card transition hover:bg-accent/5">
                                        <ChevronRight className="h-5 w-5" />
                                    </button>

                                </div>
                            </div>
                        </div>
                    </section>

                )}

                {/* ===== PRICING ===== */}
                {show.pricing && (
                    <section id="pricing" className="border-b border-border/40 bg-secondary/20">
                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:py-24">
                            <Reveal>
                                <div className="mx-auto max-w-2xl text-center">
                                    <Badge tone="accent" className="mb-4 gap-1.5">
                                        <TrendingUp className="size-3.5" />
                                        {t('landing.pricing.badge', 'Pricing')}
                                    </Badge>
                                    <h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                                        {landing.pricing_title || t('landing.pricing.title', 'Plans that grow with you')}
                                    </h2>
                                    <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                                        {landing.pricing_subtitle || t('landing.pricing.subtitle', 'Start free, upgrade when you need more sends. No contracts, no hidden fees.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className={cn(
                                'mt-16 grid gap-6',
                                plans.length === 1 ? 'mx-auto max-w-md' :
                                    plans.length === 2 ? 'mx-auto max-w-3xl grid-cols-1 md:grid-cols-2' :
                                        'sm:grid-cols-2 lg:grid-cols-3',
                            )}>
                                {plans.map((plan, i) => (
                                    <Reveal key={plan.id} delay={i * 80}>
                                        <PlanCard plan={plan} isAuthed={isAuthed} dashboardHref={dashboardHref} signupHref={signupHref} />
                                    </Reveal>
                                ))}
                            </div>

                            <p className="mt-10 text-center text-xs text-muted-foreground">
                                {landing.pricing_footnote || t('landing.pricing.footnote', 'All plans include DKIM signing, bounce + complaint processing, REST API access, and unlimited team members.')}
                            </p>
                        </div>
                    </section>
                )}

                {/* ===== FAQ ===== */}
                {show.faq && (
                    <section id="faq" className="border-b border-border/40">
                        <div className="mx-auto max-w-4xl px-6 py-20 sm:py-24">
                            <Reveal>
                                <div className="text-center">
                                    <h2 className="text-balance text-2xl font-semibold sm:text-4xl md:text-5xl">
                                        {landing.faq_title || t('landing.faq.title', 'Frequently asked questions')}
                                    </h2>
                                    <p className="lg:mt-4 mt-2 text-muted-foreground">
                                        {landing.faq_subtitle || t('landing.faq.subtitle', 'The things prospects ask most often.')}
                                    </p>
                                </div>
                            </Reveal>

                            <div className="mt-12 space-y-4">
                                {faqsList.map((f, i) => (
                                    <Reveal key={i} delay={i * 50}>
                                        <details className="group rounded-lg border border-border bg-card open:shadow-sm">
                                            <summary className="flex cursor-pointer items-center justify-between gap-2 lg:px-5 px-3 lg:py-4 py-2 text-base lg:text-lg font-medium">
                                                <span>{f.q}</span>
                                                <span className="grid size-6 place-items-center rounded-md bg-accent/15 text-muted-foreground transition-transform group-open:bg-accent group-open:text-white group-open:rotate-45">
                                                    <span className="text-lg leading-none"><LucideIcons.Plus className="size-4" /></span>
                                                </span>
                                            </summary>
                                            <div className="border-t border-border px-5 py-4 text-base text-muted-foreground">{f.a}</div>
                                        </details>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    </section>

                )}

                {/* ===== FINAL CTA ===== */}
                {show.cta && (
                    <section className="relative overflow-hidden border-b border-border/40 bg-card">
                        {/* Top hairline accent rule + layered radial glows. */}
                        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
                            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
                            <div className="absolute inset-0 [background:radial-gradient(circle_at_30%_50%,hsl(var(--primary-h)_var(--primary-s)_var(--primary-l)/0.18),transparent_55%),radial-gradient(circle_at_75%_30%,hsl(var(--primary-h)_var(--primary-s)_var(--primary-l)/0.12),transparent_55%)]" />
                            <div className="blob-1 absolute left-[30%] top-[20%] size-80 rounded-full bg-accent/20 blur-3xl" />
                            <div className="blob-2 absolute right-[18%] bottom-[15%] size-72 rounded-full bg-violet-500/15 blur-3xl" />
                        </div>

                        <div className="mx-auto max-w-5xl px-6 py-20 text-center sm:py-24">
                            <Reveal>
                                <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
                                    <span className="inline-flex size-1.5 rounded-full bg-accent shadow-[0_0_8px] shadow-accent/60" />
                                    {t('landing.cta_section.eyebrow', 'Ready to ship')}
                                </div>
                                <h2 className="mt-5 text-balance text-2xl font-semibold tracking-tight sm:text-4xl md:text-5xl lg:text-[56px] lg:leading-[1.05]">
                                    {landing.cta_section_title}
                                </h2>
                                <p className="mx-auto lg:mt-4 mt-2 max-w-2xl text-balance text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                                    {landing.cta_section_subtitle || t('landing.cta_section.subtitle', `Join the teams already using ${siteName} to ship campaigns that reach the inbox.`, { name: siteName })}
                                </p>

                                <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                                    <Link href={ctaHref}>
                                        <Button variant="accent" size="lg" className="shadow-lg shadow-accent/20">
                                            {landing.cta_button_primary || heroPrimary}
                                            <ArrowRight className="size-4" />
                                        </Button>
                                    </Link>
                                    {!isAuthed && (
                                        <Link href="/login">
                                            <Button variant="outline" size="lg">{landing.cta_button_secondary || t('landing.nav.sign_in', 'Sign in')}</Button>
                                        </Link>
                                    )}
                                </div>

                                {/* Trust microcopy — addresses the "what's the catch?" beat. */}
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Check className="size-3.5 text-success" strokeWidth={2.5} />
                                        {t('landing.cta_section.trust_no_card', 'No credit card')}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Check className="size-3.5 text-success" strokeWidth={2.5} />
                                        {t('landing.cta_section.trust_cancel', 'Cancel anytime')}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Check className="size-3.5 text-success" strokeWidth={2.5} />
                                        {t('landing.cta_section.trust_support', '6 months support included')}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Check className="size-3.5 text-success" strokeWidth={2.5} />
                                        {t('landing.cta_section.trust_self_hosted', 'Self-hosted, your data')}
                                    </span>
                                </div>
                            </Reveal>
                        </div>
                    </section>

                )}

                {/* ===== FOOTER ===== */}
                <footer className="relative z-[1]">
                    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-b from-background via-card/40 to-background" />
                        <div className="absolute -top-32 left-1/2 size-[640px] -translate-x-1/2 rounded-full bg-accent/10 blur-3xl" />
                        <div className="absolute -bottom-24 right-[8%] size-80 rounded-full bg-violet-500/10 blur-3xl" />
                        <div className="absolute -bottom-24 left-[6%] size-72 rounded-full bg-purple-500/10 blur-3xl" />
                    </div>

                    {show.newsletter && (
                        <div className="border-y border-border/80 bg-card/40 backdrop-blur-sm">
                            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-12">
                                <div className="min-w-0 max-w-md">
                                    <h4 className="text-2xl font-semibold tracking-tight text-foreground">
                                        {landing.newsletter_title || t('landing.footer.newsletter_title', 'Stay in the loop')}
                                    </h4>
                                    <p className="mt-1.5 text-sm text-muted-foreground">
                                        {landing.newsletter_subtitle || t('landing.footer.newsletter_subtitle', "Product updates, deliverability tips, and the occasional case study. No spam — we send our own email, you know we won't.")}
                                    </p>
                                </div>
                                <form className="flex w-full max-w-md flex-1 gap-2 sm:w-auto">
                                    <input
                                        type="email"
                                        placeholder={landing.newsletter_placeholder || t('landing.footer.newsletter_placeholder', 'you@yourcompany.com')}
                                        className="flex h-11 w-full rounded-lg border border-border bg-background px-3.5 text-sm shadow-xs outline-none transition-colors focus:border-accent/50 focus:ring-2 focus:ring-accent/20"
                                    />
                                    <Button variant="accent" size="default" type="submit" className="shrink-0 px-4">
                                        {landing.newsletter_button_label || t('landing.footer.newsletter_button', 'Subscribe')}
                                        <ArrowRight className="size-4" />
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="mx-auto max-w-7xl px-6 pt-16 pb-8">
                        <div className="grid lg:gap-12 gap-8 sm:grid-cols-2 lg:grid-cols-12">
                            <div className="lg:col-span-4">
                                <BrandLogo name={siteName} logo={branding.site_logo} />
                                <p className="lg:mt-4 mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{tagline}</p>
                                <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-success/30 bg-success/5 px-3 py-1.5 text-xs backdrop-blur-sm">
                                    <span className="relative inline-flex size-2 text-success pulse-dot">
                                        <span className="size-2 rounded-full bg-success" />
                                    </span>
                                    <span className="font-medium text-success">{t('landing.footer.status_operational', 'All systems operational')}</span>
                                </div>

                                <div className="mt-7 flex items-center gap-3 sm:hidden">
                                    <LocaleSwitcher />
                                    <button
                                        type="button"
                                        onClick={toggleTheme}
                                        aria-label={t('common.label.toggle_theme', 'Toggle theme')}
                                        className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-card/60 text-foreground transition-all hover:border-accent/40 hover:text-accent"
                                    >
                                        {isDark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
                                    </button>
                                </div>
                            </div>

                            <FooterLinkColumn
                                title={landing.footer_product_title || t('landing.footer.col_product', 'Product')}
                                links={footerProductLinks ?? [
                                    { label: t('landing.nav.features', 'Features'), href: '#features' },
                                    { label: t('landing.nav.product_tour', 'Product tour'), href: '#product' },
                                    { label: t('landing.nav.integrations', 'Integrations'), href: '#integrations' },
                                    ...(plans.length > 0 && billing_enabled ? [{ label: t('landing.nav.pricing', 'Pricing'), href: '#pricing' }] : []),
                                    { label: t('landing.nav.faq', 'FAQ'), href: '#faq' },
                                ]}
                            />
                            <FooterLinkColumn
                                title={landing.footer_resources_title || t('landing.footer.col_resources', 'Resources')}
                                links={footerResourcesLinks ?? [
                                    { label: t('landing.footer.docs', 'Documentation'), href: '#' },
                                    { label: t('landing.footer.api_reference', 'API reference'), href: '#' },
                                    { label: t('landing.footer.deliverability_guide', 'Deliverability guide'), href: '#' },
                                    { label: t('landing.footer.status', 'Status'), href: '#' },
                                    { label: t('landing.footer.changelog', 'Changelog'), href: '#' },
                                ]}
                            />

                            <FooterLinkColumn
                                title={landing.footer_company_title || t('landing.footer.col_company', 'Company')}
                                links={footerCompanyLinks ?? [
                                    { label: t('landing.footer.about', 'About'), href: '#' },
                                    { label: t('landing.footer.contact', 'Contact'), href: '#' },
                                    { label: t('landing.footer.careers', 'Careers'), href: '#' },
                                    { label: t('landing.footer.partners', 'Partners'), href: '#' },
                                    { label: t('landing.footer.support', 'Support'), href: 'mailto:support' },
                                ]}
                            />

                            <div className="lg:col-span-2">
                                <h4 className="lg:mb-4 mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                                    {t('landing.footer.col_account', 'Account')}
                                </h4>
                                <ul className="space-y-2.5 text-sm">
                                    {!isAuthed ? (
                                        <>
                                            <li><Link href="/login" className="text-muted-foreground transition-colors hover:text-foreground">{t('landing.nav.sign_in', 'Sign in')}</Link></li>
                                            <li><Link href="/login" className="text-muted-foreground transition-colors hover:text-foreground">{t('landing.footer.create_account', 'Create account')}</Link></li>
                                            <li><Link href="/password/forgot" className="text-muted-foreground transition-colors hover:text-foreground">{t('landing.footer.forgot_password', 'Forgot password')}</Link></li>
                                        </>
                                    ) : (
                                        <li><Link href={dashboardHref} className="text-muted-foreground transition-colors hover:text-foreground">{t('landing.footer.dashboard', 'Dashboard')}</Link></li>
                                    )}
                                    <li><Link href="/admin/login" className="text-muted-foreground transition-colors hover:text-foreground">{t('landing.footer.admin_login', 'Admin login')}</Link></li>
                                </ul>
                            </div>
                        </div>

                        {/* Legal + social row */}
                        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border/80 pt-6 text-xs text-muted-foreground">
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                <span>{copyright}</span>
                                <span className="text-border">·</span>
                                {(footerLegalLinks ?? [
                                    { label: t('landing.footer.legal_privacy', 'Privacy'), href: '#' },
                                    { label: t('landing.footer.legal_terms', 'Terms'), href: '#' },
                                    { label: t('landing.footer.legal_cookies', 'Cookies'), href: '#' },
                                    { label: t('landing.footer.legal_gdpr', 'GDPR'), href: '#' },
                                    { label: t('landing.footer.legal_canspam', 'CAN-SPAM'), href: '#' },
                                ]).map((l, i) => {
                                    const target = l.target === '_blank' ? '_blank' : '_self';
                                    return (
                                        <a
                                            key={l.label || i}
                                            href={l.href || '#'}
                                            target={target}
                                            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                                            className="transition-colors hover:text-foreground"
                                        >
                                            {l.label}
                                        </a>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-2.5">
                                {(footerSocialLinks ?? [
                                    { platform: 'twitter', href: '#' },
                                    { platform: 'linkedin', href: '#' },
                                    { platform: 'github', href: '#' },
                                ]).map((s, i) => {
                                    const meta = SOCIAL_GLYPHS[s.platform] ?? SOCIAL_GLYPHS.link;
                                    return (
                                        <SocialIcon key={i} href={s.href || '#'} label={meta.label}>
                                            <SocialGlyph platform={s.platform} />
                                        </SocialIcon>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </footer>
            </div>

            <HeroVideoModal
                open={tourVideoOpen}
                onClose={() => setTourVideoOpen(false)}
                url={tourVideoUrl}
                title={landing.hero_cta_secondary || t('landing.product.badge', 'Product tour')}
            />
        </>
    );
}

function FooterLinkColumn({ title, links }) {
    return (
        <div className="lg:col-span-2">
            <h4 className="lg:mb-4 mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</h4>
            <ul className="space-y-2.5 text-sm">
                {(links ?? []).map((l, i) => {
                    const target = l.target === '_blank' ? '_blank' : '_self';
                    return (
                        <li key={l.label || i}>
                            <a
                                href={l.href || '#'}
                                target={target}
                                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                                className="text-muted-foreground transition-colors hover:text-foreground"
                            >
                                {l.label}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function SocialIcon({ href, label, children }) {
    return (
        <a
            href={href}
            aria-label={label}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative grid size-9 place-items-center rounded-lg border border-border bg-card/60 text-muted-foreground backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/50 hover:bg-accent/5 hover:text-accent hover:shadow-md hover:shadow-accent/10"
        >
            {children}
        </a>
    );
}

const SOCIAL_GLYPHS = {
    twitter: { label: 'Twitter', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
    linkedin: { label: 'LinkedIn', path: 'M20.452 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.554V9h3.414v1.561h.048c.476-.9 1.637-1.852 3.37-1.852 3.604 0 4.27 2.37 4.27 5.455v6.288zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zM7.114 20.452H3.558V9h3.556v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0z' },
    github: { label: 'GitHub', path: 'M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z' },
    facebook: { label: 'Facebook', path: 'M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z' },
    instagram: { label: 'Instagram', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.069 1.646.069 4.85s-.012 3.584-.07 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.069-4.85.069s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311 1.266-.058 1.646-.069 4.85-.069zM12 0C8.741 0 8.332.014 7.052.072c-1.834.084-3.46.498-4.756 1.795C.998 3.164.584 4.79.5 6.624.442 7.904.428 8.313.428 11.572c0 3.259.014 3.668.072 4.948.084 1.834.498 3.46 1.795 4.756 1.297 1.297 2.923 1.711 4.757 1.795 1.28.058 1.689.072 4.948.072s3.668-.014 4.948-.072c1.834-.084 3.46-.498 4.756-1.795 1.297-1.297 1.711-2.923 1.795-4.756.058-1.28.072-1.689.072-4.948s-.014-3.668-.072-4.948c-.084-1.834-.498-3.46-1.795-4.757C20.408.998 18.782.584 16.948.5 15.668.442 15.259.428 12 .428zm0 5.838a5.734 5.734 0 1 0 0 11.468 5.734 5.734 0 0 0 0-11.468zm0 9.45a3.716 3.716 0 1 1 0-7.432 3.716 3.716 0 0 1 0 7.432zm5.846-9.74a1.34 1.34 0 1 0 0 2.68 1.34 1.34 0 0 0 0-2.68z' },
    youtube: { label: 'YouTube', path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
    link: { label: 'Link', path: 'M10.586 13.414a1 1 0 0 1 0-1.414l3-3a3 3 0 0 1 4.243 0l1.414 1.414a3 3 0 0 1 0 4.243l-3 3a3 3 0 0 1-4.243 0l-.586-.586 1.414-1.414.586.586a1 1 0 0 0 1.414 0l3-3a1 1 0 0 0 0-1.414L16.414 10.586a1 1 0 0 0-1.414 0l-3 3a1 1 0 0 1-1.414 0zM13.414 10.586a1 1 0 0 1 0 1.414l-3 3a3 3 0 0 1-4.243 0L4.757 13.586a3 3 0 0 1 0-4.243l3-3a3 3 0 0 1 4.243 0l.586.586-1.414 1.414-.586-.586a1 1 0 0 0-1.414 0l-3 3a1 1 0 0 0 0 1.414L7.586 13.414a1 1 0 0 0 1.414 0l3-3a1 1 0 0 1 1.414 0z' },
};

function SocialGlyph({ platform }) {
    const data = SOCIAL_GLYPHS[platform] ?? SOCIAL_GLYPHS.link;
    return (
        <svg viewBox="0 0 24 24" className="size-4" fill="currentColor">
            <path d={data.path} />
        </svg>
    );
}

function BrandLogo() {
    return (
        <Link href="/" className="inline-flex items-center font-semibold">
            <BrandMark size="md" />
        </Link>
    );
}

function PlanCard({ plan, isAuthed, dashboardHref, signupHref = '/login' }) {
    const { t } = useT();
    const price = plan.default_price;
    const featured = plan.is_featured;
    const planFeatures = Array.isArray(plan.features) ? plan.features : [];
    const ctaHref = isAuthed ? dashboardHref : signupHref;
    const ctaLabel = isAuthed ? t('landing.cta.open_dashboard', 'Open dashboard') : (price?.trial_days > 0 ? t('landing.pricing.start_trial_days', `Start ${price.trial_days}-day trial`, { days: price.trial_days }) : t('landing.pricing.get_started', 'Get started'));

    return (
        <div className={cn(
            'relative h-full rounded-2xl border bg-card p-7 transition-all duration-300',
            featured
                ? 'border-accent shadow-2xl shadow-accent/15 ring-2 ring-accent/30 lg:-translate-y-2'
                : 'border-border hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md',
        )}>
            {featured && (
                <Badge tone="accent" className="absolute -top-3 left-1/2 -translate-x-1/2 gap-1.5 px-3 py-1 shadow-md shadow-accent/30">
                    <Star className="size-3 fill-current" />
                    {t('landing.pricing.most_popular', 'Most popular')}
                </Badge>
            )}

            <h3 className="text-xl font-bold tracking-tight">{plan.name}</h3>
            {plan.description && <p className="mt-1.5 text-sm text-muted-foreground">{plan.description}</p>}

            <div className="mt-6 flex items-baseline gap-1.5">
                <span className="text-4xl font-bold tracking-tight tabular-nums">{price?.formatted ?? '—'}</span>
                {price && <span className="text-sm text-muted-foreground">{intervalLabel(price)}</span>}
            </div>

            {price?.trial_days > 0 && (
                <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-success">
                    <Zap className="size-3" />
                    {t('landing.pricing.trial_included', `${price.trial_days}-day free trial included`, { days: price.trial_days })}
                </p>
            )}

            <Link href={ctaHref} className="mt-6 block">
                <Button variant={featured ? 'accent' : 'outline'} size="lg" className="w-full">
                    {ctaLabel}
                    <ArrowRight className="size-4" />
                </Button>
            </Link>

            {planFeatures.length > 0 && (
                <ul className="mt-7 space-y-2.5 border-t border-border pt-6 text-sm">
                    {planFeatures.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                            <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                                <Check className="size-3" strokeWidth={3} />
                            </span>
                            <span className="text-muted-foreground">{typeof f === 'string' ? f : f.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
